import type { Contract } from '../api/types'
import { localeCorrente, t } from '../i18n/lingua'

/**
 * Ciò che le pagine dei contratti condividono: etichette dei cataloghi (tipi,
 * stati, periodicità), importi, la cella della «data che conta».
 *
 * Le etichette si traducono con `t('…')` letterale per valore, non con
 * `t(valore)`: il testo che arriva dal server passerebbe come variabile e
 * sfuggirebbe alla guardia delle traduzioni.
 */
export function etichettaTipoContratto(tipo: string): string {
  switch (tipo) {
    case 'maintenance': return t('Manutenzione hardware')
    case 'support': return t('Supporto e assistenza')
    case 'lease': return t('Noleggio o leasing')
    case 'license': return t('Licenza software')
    case 'extended_support': return t('Supporto esteso')
    case 'warranty': return t('Garanzia estesa')
    case 'subscription': return t('Abbonamento a servizio')
    case 'saas': return t('Software come servizio (SaaS)')
    case 'cloud': return t('Cloud (IaaS/PaaS)')
    case 'service': return t('Servizio gestito')
    case 'consulting': return t('Consulenza')
    case 'connectivity': return t('Connettività e telecomunicazioni')
    case 'other': return t('Altro')
    default: return tipo
  }
}

export function etichettaStatoContratto(stato: string): string {
  switch (stato) {
    case 'draft': return t('Bozza')
    case 'active': return t('In vigore')
    case 'suspended': return t('Sospeso')
    case 'terminated': return t('Cessato')
    case 'expired': return t('Scaduto')
    case 'renewed': return t('Rinnovato')
    default: return stato
  }
}

export function etichettaPeriodicita(periodo: string): string {
  switch (periodo) {
    case 'annual': return t('Annuale')
    case 'monthly': return t('Mensile')
    case 'quarterly': return t('Trimestrale')
    case 'one_off': return t('Una tantum')
    default: return periodo
  }
}

export function etichettaEventoContratto(kind: string): string {
  switch (kind) {
    case 'created': return t('Creato')
    case 'updated': return t('Modificato')
    case 'renewed': return t('Rinnovato')
    case 'terminated': return t('Cessato')
    case 'reminder': return t('Promemoria inviato')
    case 'note': return t('Nota')
    case 'coverage': return t('Copertura')
    default: return kind
  }
}

/** Il campo del contratto, per lo storico delle modifiche. */
export function etichettaCampoContratto(campo: string): string {
  switch (campo) {
    case 'reference': return t('Riferimento')
    case 'name': return t('Nome')
    case 'contract_type': return t('Tipo')
    case 'status': return t('Stato')
    case 'vendor': return t('Fornitore')
    case 'vendor_contact': return t('Contatto del fornitore')
    case 'start_date': return t('Decorrenza')
    case 'end_date': return t('Scadenza')
    case 'auto_renew': return t('Rinnovo tacito')
    case 'notice_days': return t('Giorni di preavviso')
    case 'renewal_months': return t('Durata del rinnovo (mesi)')
    case 'value': return t('Valore')
    case 'currency': return t('Valuta')
    case 'billing_period': return t('Periodicità')
    case 'payment_terms': return t('Termini di pagamento')
    case 'external_ref': return t('Riferimento esterno')
    case 'service_level': return t('Livello di servizio')
    case 'cost_center_id': return t('Centro di costo')
    case 'org_unit_id': return t('Unità organizzativa')
    case 'location_id': return t('Sede')
    case 'owner': return t('Responsabile')
    case 'owner_email': return t('Email del responsabile')
    case 'eagle_sla_id': return t('SLA di Eagle')
    case 'reminder_days': return t('Promemoria (giorni prima)')
    case 'items': return t('Voci')
    case 'documents': return t('Documenti')
    case 'notes': return t('Note')
    default: return campo
  }
}

export function tonoStato(stato: string): 'ok' | 'warn' | 'error' | 'muted' | 'info' {
  switch (stato) {
    case 'active': return 'ok'
    case 'expired': return 'error'
    case 'suspended': return 'warn'
    case 'draft': return 'info'
    default: return 'muted'
  }
}

export function BadgeStatoContratto({ stato }: { stato: string }) {
  const tono = tonoStato(stato)
  const classe = tono === 'ok' ? 'badge badge--ok' : tono === 'warn' ? 'badge badge--warn'
    : tono === 'error' ? 'badge badge--error' : tono === 'info' ? 'badge badge--info' : 'badge badge--muted'
  return <span className={classe}>{etichettaStatoContratto(stato)}</span>
}

export function formattaImporto(valore: number | null | undefined, valuta: string): string {
  if (valore === null || valore === undefined) return '—'
  try {
    return valore.toLocaleString(localeCorrente(), { style: 'currency', currency: valuta || 'EUR', maximumFractionDigits: 2 })
  } catch {
    return `${valore.toLocaleString(localeCorrente())} ${valuta}`
  }
}

/** Il colore della data che conta: rosso passata, ambra entro due mesi. */
export function coloreScadenza(giorni: number | null): string | undefined {
  if (giorni === null) return undefined
  if (giorni < 0) return 'var(--error)'
  if (giorni < 60) return 'var(--warn)'
  return undefined
}

/** La «data che conta» con il perché: su rinnovo tacito è mesi prima della scadenza. */
export function CellaScadenza({ contratto }: { contratto: Pick<Contract, 'deadline' | 'deadline_reason' | 'days_left' | 'auto_renew'> }) {
  const giorni = contratto.days_left
  if (!contratto.deadline) return <span className="text-muted">{t('senza scadenza')}</span>
  return (
    <>
      <strong style={{ color: coloreScadenza(giorni) }}>{contratto.deadline}</strong>
      <div className="text-muted" style={{ fontSize: 11 }}>
        {giorni === null
          ? motivoScadenza(contratto.deadline_reason)
          : giorni < 0
            ? t('passata da {n} giorni', { n: Math.abs(giorni) })
            : t('fra {n} giorni · {motivo}', { n: giorni, motivo: motivoScadenza(contratto.deadline_reason) })}
      </div>
    </>
  )
}

/** Il motivo arriva dal server in italiano: si riconosce e si traduce. */
export function motivoScadenza(motivo: string): string {
  if (motivo === 'scadenza') return t('scadenza')
  if (motivo === 'senza scadenza') return t('senza scadenza')
  if (motivo.startsWith('si rinnova tacitamente')) return t('si rinnova tacitamente a questa data')
  const m = /\((\d+) giorni/.exec(motivo)
  if (m) return t('ultimo giorno per disdire ({n} giorni di preavviso)', { n: Number(m[1]) })
  return motivo
}
